import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  Badge,
  Flex,
  Grid,
  GridItem,
  Select,
  InputGroup,
  InputLeftElement,
  Input,
  Icon,
  Button,
  HStack,
  IconButton,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Textarea,
  useDisclosure,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  VStack,
} from '@chakra-ui/react';
import Card from 'components/card/Card';
import {
  MdSearch,
  MdSchool,
  MdList,
  MdAttachMoney,
  MdAdd,
  MdEdit,
  MdBlock,
  MdChevronLeft,
  MdChevronRight,
} from 'react-icons/md';
import httpClient from 'utils/httpClient';
import {
  getAllScholarships,
  getScholarshipTypes,
  getScholarshipSummary,
  assignScholarship,
  updateScholarship,
  revokeScholarship,
} from 'services/scholarship.service';

const PAGE_SIZE = 15;

const statusBadge = {
  ACTIVE: { label: 'Activa', color: 'green' },
  SUSPENDED: { label: 'Suspendida', color: 'yellow' },
  EXPIRED: { label: 'Vencida', color: 'gray' },
  REVOKED: { label: 'Revocada', color: 'red' },
};

function formatDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleDateString('es-UY');
}

export default function ScholarshipView() {
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const rowHover = useColorModeValue('gray.50', 'whiteAlpha.50');

  const [scholarships, setScholarships] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(0);
  const [types, setTypes] = useState([]);
  const [students, setStudents] = useState([]);
  const toast = useToast();

  const assignModal = useDisclosure();
  const editModal = useDisclosure();
  const revokeDialog = useDisclosure();
  const cancelRef = React.useRef();

  const [assignForm, setAssignForm] = useState({
    studentId: '',
    scholarshipTypeId: '',
    startDate: '',
    endDate: '',
    customPercentage: '',
    conditions: '',
    notes: '',
  });

  const [editForm, setEditForm] = useState({
    id: null,
    status: '',
    notes: '',
  });

  const [revokeId, setRevokeId] = useState(null);

  const loadStudents = async () => {
    try {
      const res = await httpClient.get('/person/student?page=1&limit=3000');
      const people = res?.payload?.people || res?.payload?.data || [];
      setStudents(Array.isArray(people) ? people : []);
    } catch (e) {
      console.error(e);
      setStudents([]);
    }
  };

  const loadTypes = async () => {
    try {
      const res = await getScholarshipTypes();
      const data = res?.payload?.data || [];
      setTypes(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      setTypes([]);
    }
  };

  const loadSummary = async () => {
    try {
      const res = await getScholarshipSummary();
      if (res?.status === 200) setSummary(res.payload);
    } catch (e) {
      console.error(e);
    }
  };

  const loadScholarships = async () => {
    try {
      setLoading(true);
      const filters = statusFilter ? { status: statusFilter } : {};
      const res = await getAllScholarships(filters);
      if (res?.status === 200) {
        const data = res.payload?.data || [];
        setScholarships(Array.isArray(data) ? data : []);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadScholarships();
    loadSummary();
  }, [statusFilter]);

  useEffect(() => {
    setPage(0);
  }, [searchTerm, statusFilter]);

  useEffect(() => {
    if (assignModal.isOpen) {
      loadStudents();
      loadTypes();
    }
  }, [assignModal.isOpen]);

  const filtered = useMemo(() => {
    const t = searchTerm.toLowerCase();
    return scholarships.filter((s) => (s.student_name || '').toLowerCase().includes(t));
  }, [scholarships, searchTerm]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  const totalAll = useMemo(() => {
    if (!summary?.countByStatus) return scholarships.length;
    return Object.values(summary.countByStatus).reduce((a, b) => a + Number(b || 0), 0);
  }, [summary, scholarships.length]);

  const formatMoney = (n) =>
    new Intl.NumberFormat('es-UY', {
      style: 'currency',
      currency: 'UYU',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(Number(n) || 0);

  const coveragePct = (row) => {
    const c = row.custom_percentage != null ? Number(row.custom_percentage) : null;
    const t = row.type_coverage_percentage != null ? Number(row.type_coverage_percentage) : null;
    if (c != null && !Number.isNaN(c)) return c;
    if (t != null && !Number.isNaN(t)) return t;
    return null;
  };

  const openEdit = (row) => {
    setEditForm({ id: row.id, status: row.status || 'ACTIVE', notes: row.notes || '' });
    editModal.onOpen();
  };

  const submitEdit = async () => {
    try {
      await updateScholarship(editForm.id, {
        status: editForm.status,
        notes: editForm.notes,
        updatedBy: 1,
      });
      toast({ title: 'Beca actualizada', status: 'success', duration: 2500, isClosable: true });
      editModal.onClose();
      loadScholarships();
      loadSummary();
    } catch (e) {
      toast({ title: 'Error', description: e.message || 'No se pudo actualizar', status: 'error', duration: 4000, isClosable: true });
    }
  };

  const confirmRevoke = async () => {
    if (!revokeId) return;
    try {
      await revokeScholarship(revokeId, { updatedBy: 1 });
      toast({ title: 'Beca revocada', status: 'success', duration: 2500, isClosable: true });
      revokeDialog.onClose();
      setRevokeId(null);
      loadScholarships();
      loadSummary();
    } catch (e) {
      toast({ title: 'Error', description: e.message || 'No se pudo revocar', status: 'error', duration: 4000, isClosable: true });
    }
  };

  const submitAssign = async () => {
    if (!assignForm.studentId || !assignForm.scholarshipTypeId || !assignForm.startDate) {
      toast({ title: 'Complete estudiante, tipo y fecha de inicio', status: 'warning', duration: 3000, isClosable: true });
      return;
    }
    try {
      await assignScholarship({
        studentId: Number(assignForm.studentId),
        scholarshipTypeId: Number(assignForm.scholarshipTypeId),
        startDate: assignForm.startDate,
        endDate: assignForm.endDate || undefined,
        customPercentage: assignForm.customPercentage === '' ? undefined : Number(assignForm.customPercentage),
        conditions: assignForm.conditions || undefined,
        notes: assignForm.notes || undefined,
        createdBy: 1,
      });
      toast({ title: 'Beca asignada', status: 'success', duration: 2500, isClosable: true });
      assignModal.onClose();
      setAssignForm({
        studentId: '',
        scholarshipTypeId: '',
        startDate: '',
        endDate: '',
        customPercentage: '',
        conditions: '',
        notes: '',
      });
      loadScholarships();
      loadSummary();
    } catch (e) {
      toast({ title: 'Error', description: e.message || 'No se pudo asignar', status: 'error', duration: 4000, isClosable: true });
    }
  };

  const summaryCards = [
    {
      icon: MdSchool,
      label: 'Total becas activas',
      value: summary?.totalActiveScholarships ?? summary?.countByStatus?.ACTIVE ?? 0,
      color: 'green.500',
      bg: useColorModeValue('green.50', 'green.900'),
    },
    {
      icon: MdList,
      label: 'Total becas (todas)',
      value: totalAll,
      color: 'blue.500',
      bg: useColorModeValue('blue.50', 'blue.900'),
    },
    {
      icon: MdAttachMoney,
      label: 'Cobertura estimada',
      value: formatMoney(summary?.coverageAmountEstimate ?? 0),
      color: 'purple.500',
      bg: useColorModeValue('purple.50', 'purple.900'),
    },
  ];

  const studentOptions = students
    .filter((p) => p.student?.id != null)
    .map((p) => {
      const sid = p.student.id;
      const last = p.lastName || p.last_name || '';
      const label = `${p.name || ''} ${last}`.trim() || p.document || `Estudiante ${sid}`;
      return { value: sid, label };
    });

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
      <Grid templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }} gap={4} mb={6}>
        {summaryCards.map((c, i) => (
          <GridItem key={i}>
            <Card bg={c.bg} p={5}>
              <Flex align="center">
                <Icon as={c.icon} w={6} h={6} color={c.color} mr={3} />
                <Stat>
                  <StatLabel fontSize="xs" color="gray.500">
                    {c.label}
                  </StatLabel>
                  <StatNumber fontSize="lg" color={c.color}>
                    {c.value}
                  </StatNumber>
                  <StatHelpText fontSize="xs" mb={0}>
                    Resumen becas
                  </StatHelpText>
                </Stat>
              </Flex>
            </Card>
          </GridItem>
        ))}
      </Grid>

      <Card direction="column" w="100%" px="0px" overflowX={{ sm: 'scroll', lg: 'hidden' }}>
        <Flex px="25px" justify="space-between" mb="20px" align="center" flexWrap="wrap" gap={4}>
          <Text color={textColor} fontSize="22px" fontWeight="700" lineHeight="100%">
            Becas
          </Text>
          <Button size="sm" leftIcon={<MdAdd />} colorScheme="brand" onClick={assignModal.onOpen}>
            Asignar Beca
          </Button>
        </Flex>

        <Flex px="25px" mb="20px" gap={4} flexWrap="wrap">
          <InputGroup maxW="300px">
            <InputLeftElement pointerEvents="none">
              <Icon as={MdSearch} color="gray.300" />
            </InputLeftElement>
            <Input
              placeholder="Buscar por estudiante..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </InputGroup>
          <Select
            maxW="220px"
            placeholder="Todos los estados"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="ACTIVE">Activa</option>
            <option value="SUSPENDED">Suspendida</option>
            <option value="EXPIRED">Vencida</option>
            <option value="REVOKED">Revocada</option>
          </Select>
        </Flex>

        {!loading && (
          <>
            <Box overflowX="auto">
              <Table variant="simple" color="gray.500" mb="12px" size="sm">
                <Thead>
                  <Tr>
                    <Th borderColor={borderColor} fontSize="xs">
                      Estudiante
                    </Th>
                    <Th borderColor={borderColor} fontSize="xs">
                      Tipo de Beca
                    </Th>
                    <Th borderColor={borderColor} fontSize="xs" isNumeric>
                      Cobertura %
                    </Th>
                    <Th borderColor={borderColor} fontSize="xs">
                      Estado
                    </Th>
                    <Th borderColor={borderColor} fontSize="xs">
                      Fecha inicio
                    </Th>
                    <Th borderColor={borderColor} fontSize="xs">
                      Acciones
                    </Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {paginated.map((row) => {
                    const st = statusBadge[row.status] || { label: row.status, color: 'gray' };
                    const pct = coveragePct(row);
                    return (
                      <Tr key={row.id} _hover={{ bg: rowHover }}>
                        <Td borderColor={borderColor}>
                          <Text fontWeight="600" fontSize="sm" color={textColor}>
                            {row.student_name}
                          </Text>
                        </Td>
                        <Td borderColor={borderColor}>
                          <Text fontSize="sm">{row.scholarship_name}</Text>
                        </Td>
                        <Td borderColor={borderColor} isNumeric>
                          <Text fontSize="sm">{pct != null ? `${pct}%` : '—'}</Text>
                        </Td>
                        <Td borderColor={borderColor}>
                          <Badge colorScheme={st.color} fontSize="xs" borderRadius="6px" px={2}>
                            {st.label}
                          </Badge>
                        </Td>
                        <Td borderColor={borderColor}>
                          <Text fontSize="sm">{formatDate(row.start_date)}</Text>
                        </Td>
                        <Td borderColor={borderColor}>
                          <HStack spacing={1}>
                            <IconButton
                              size="xs"
                              icon={<MdEdit />}
                              colorScheme="blue"
                              variant="ghost"
                              aria-label="Editar"
                              onClick={() => openEdit(row)}
                            />
                            <IconButton
                              size="xs"
                              icon={<MdBlock />}
                              colorScheme="red"
                              variant="ghost"
                              aria-label="Revocar"
                              isDisabled={row.status === 'REVOKED'}
                              onClick={() => {
                                setRevokeId(row.id);
                                revokeDialog.onOpen();
                              }}
                            />
                          </HStack>
                        </Td>
                      </Tr>
                    );
                  })}
                </Tbody>
              </Table>
            </Box>

            <Flex px="25px" pb={4} justify="space-between" align="center">
              <Text fontSize="xs" color="gray.500">
                Mostrando {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, filtered.length)} de {filtered.length}
              </Text>
              <HStack spacing={2}>
                <IconButton
                  size="sm"
                  icon={<MdChevronLeft />}
                  isDisabled={page === 0}
                  onClick={() => setPage(page - 1)}
                  variant="outline"
                  aria-label="Anterior"
                />
                <Text fontSize="sm" fontWeight="600">
                  {page + 1} / {totalPages}
                </Text>
                <IconButton
                  size="sm"
                  icon={<MdChevronRight />}
                  isDisabled={page >= totalPages - 1}
                  onClick={() => setPage(page + 1)}
                  variant="outline"
                  aria-label="Siguiente"
                />
              </HStack>
            </Flex>
          </>
        )}

        {!loading && filtered.length === 0 && (
          <Flex justify="center" align="center" py={10} direction="column">
            <Icon as={MdSchool} w={12} h={12} color="gray.300" mb={3} />
            <Text color="gray.500" fontSize="md">
              {searchTerm || statusFilter ? 'No hay becas con esos filtros' : 'No hay becas registradas'}
            </Text>
          </Flex>
        )}
      </Card>

      <Modal isOpen={assignModal.isOpen} onClose={assignModal.onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Asignar beca</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <FormControl isRequired>
                <FormLabel>Estudiante</FormLabel>
                <Select
                  placeholder="Seleccionar estudiante"
                  value={assignForm.studentId}
                  onChange={(e) => setAssignForm((f) => ({ ...f, studentId: e.target.value }))}
                >
                  {studentOptions.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </Select>
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Tipo de beca</FormLabel>
                <Select
                  placeholder="Seleccionar tipo"
                  value={assignForm.scholarshipTypeId}
                  onChange={(e) => setAssignForm((f) => ({ ...f, scholarshipTypeId: e.target.value }))}
                >
                  {types.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name} ({t.coverage_percentage ?? t.coveragePercentage}%)
                    </option>
                  ))}
                </Select>
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Fecha inicio</FormLabel>
                <Input
                  type="date"
                  value={assignForm.startDate}
                  onChange={(e) => setAssignForm((f) => ({ ...f, startDate: e.target.value }))}
                />
              </FormControl>
              <FormControl>
                <FormLabel>Fecha fin (opcional)</FormLabel>
                <Input
                  type="date"
                  value={assignForm.endDate}
                  onChange={(e) => setAssignForm((f) => ({ ...f, endDate: e.target.value }))}
                />
              </FormControl>
              <FormControl>
                <FormLabel>Porcentaje personalizado (opcional)</FormLabel>
                <Input
                  type="number"
                  placeholder="Ej: 75"
                  value={assignForm.customPercentage}
                  onChange={(e) => setAssignForm((f) => ({ ...f, customPercentage: e.target.value }))}
                />
              </FormControl>
              <FormControl>
                <FormLabel>Condiciones</FormLabel>
                <Textarea
                  value={assignForm.conditions}
                  onChange={(e) => setAssignForm((f) => ({ ...f, conditions: e.target.value }))}
                  rows={3}
                />
              </FormControl>
              <FormControl>
                <FormLabel>Notas</FormLabel>
                <Textarea
                  value={assignForm.notes}
                  onChange={(e) => setAssignForm((f) => ({ ...f, notes: e.target.value }))}
                  rows={3}
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={assignModal.onClose}>
              Cancelar
            </Button>
            <Button colorScheme="brand" onClick={submitAssign}>
              Guardar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={editModal.isOpen} onClose={editModal.onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Editar beca</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl mb={4}>
              <FormLabel>Estado</FormLabel>
              <Select
                value={editForm.status}
                onChange={(e) => setEditForm((f) => ({ ...f, status: e.target.value }))}
              >
                <option value="ACTIVE">Activa</option>
                <option value="SUSPENDED">Suspendida</option>
                <option value="EXPIRED">Vencida</option>
              </Select>
            </FormControl>
            <FormControl>
              <FormLabel>Notas</FormLabel>
              <Textarea
                value={editForm.notes}
                onChange={(e) => setEditForm((f) => ({ ...f, notes: e.target.value }))}
                rows={4}
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={editModal.onClose}>
              Cancelar
            </Button>
            <Button colorScheme="brand" onClick={submitEdit}>
              Actualizar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <AlertDialog isOpen={revokeDialog.isOpen} leastDestructiveRef={cancelRef} onClose={revokeDialog.onClose}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Revocar beca
            </AlertDialogHeader>
            <AlertDialogBody>¿Confirma revocar esta beca? El estado pasará a Revocada.</AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={revokeDialog.onClose}>
                Cancelar
              </Button>
              <Button colorScheme="red" onClick={confirmRevoke} ml={3}>
                Revocar
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
}
